import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/* Fichiers d'installation du connecteur.
   Pour servir les installateurs depuis metaconnect.fr, il suffit de changer ces
   deux constantes : le reste de la page n'a pas besoin d'être touché. */
const INSTALLEUR_WINDOWS =
  "https://vcheck3.icanopee.net/versions/?file=DmpConnect-JS2-1.11.14-Windows-production.exe";
const INSTALLEUR_MACOS =
  "https://vcheck3.icanopee.net/versions/?file=DmpConnect-JS2-1.11.14-universal-MacOS-production.dmg";
const VERSION = "1.11.14";

const RAISONS = [
  {
    t: "Un site web ne voit pas votre lecteur",
    d: "Pour des raisons de sécurité, une page web n'a jamais accès au matériel branché sur votre poste. Le connecteur fait ce lien, et lui seul : il tourne sur votre machine et parle à votre lecteur de carte.",
  },
  {
    t: "La carte est lue sur votre poste",
    d: "Le code porteur est saisi localement, sur votre ordinateur. Ce qui circule ensuite, ce sont les éléments d'authentification attendus par les services nationaux — jamais votre code.",
  },
  {
    t: "Il tourne en arrière-plan",
    d: "Une fois installé, le connecteur se lance avec votre session et attend. Vous n'avez plus rien à ouvrir : il répond quand une de vos applications a besoin de la carte.",
  },
];

const ETAPES = [
  {
    t: "Téléchargez le fichier",
    d: "Choisissez Windows ou macOS selon votre poste, puis ouvrez le fichier une fois le téléchargement terminé.",
  },
  {
    t: "Laissez l'installation aller au bout",
    d: "Elle prend moins d'une minute. Votre poste peut vous demander d'autoriser l'installation : c'est normal.",
  },
  {
    t: "Revenez sur votre onglet et cliquez sur Réessayer",
    d: "Inutile de recommencer votre saisie : l'application revérifie la présence du connecteur et poursuit là où vous en étiez.",
  },
];

const PREREQUIS = [
  "Windows 10 ou 11, ou macOS (processeur Intel comme Apple Silicon)",
  "Un lecteur de carte branché et reconnu par votre poste",
  "Votre carte CPS, ou votre e-CPS si vous travaillez sans lecteur",
  "Le droit d'installer un programme sur ce poste",
];

const ETATS = [
  {
    hex: "#F03B3B",
    t: "Icône rouge",
    d: "Le connecteur tourne, mais il n'est pas connecté au service. Patientez quelques secondes, vérifiez votre connexion internet, puis cliquez sur Réessayer.",
  },
  {
    hex: "#8E74CE",
    t: "Icône violette",
    d: "Le connecteur est connecté : tout est prêt. Si l'application ne le voit toujours pas, rouvrez votre navigateur, c'est lui qui doit refaire la demande.",
  },
  {
    hex: null,
    t: "Aucune icône",
    d: "Elle est peut-être seulement masquée : sous Windows, cliquez sur la flèche à gauche de l'horloge pour déplier les icônes cachées. Si elle n'y est pas non plus, le connecteur n'est pas lancé : ouvrez-le depuis vos applications, ou reprenez l'installation ci-dessus.",
  },
];

const SOUCIS = [
  {
    t: "L'icône est violette, mais rien ne se passe",
    d: "Fermez complètement votre navigateur puis rouvrez la page. Tant qu'il n'a pas refait la demande, il garde en mémoire l'échec de la première tentative.",
  },
  {
    t: "L'installation est bloquée",
    d: "Sur un poste géré par un service informatique, l'installation peut demander une autorisation. Transmettez-leur cette page : il s'agit d'un programme local, sans ouverture de port entrant.",
  },
  {
    t: "Votre antivirus intervient",
    d: "Certains antivirus filtrent les échanges entre le navigateur et les programmes locaux. Il faut alors autoriser le connecteur dans leur liste d'exceptions.",
  },
  {
    t: "Le lecteur n'est pas reconnu",
    d: "Débranchez-le, rebranchez-le, puis rouvrez votre navigateur. Si votre carte n'est toujours pas lue, le pilote du lecteur est probablement à installer.",
  },
];

export default function Download() {
  return (
    <div className="dlc">
      <SEO
        title="Télécharger le connecteur de carte CPS · MetaConnect"
        description="Le connecteur relie votre navigateur à votre lecteur de carte CPS. Téléchargement Windows et macOS, installation en trois étapes, et que faire ensuite."
        canonical="/download"
      />
      <style>{CSS}</style>

      <section className="dlc-hero">
        <div className="dlc-wrap">
          <span className="dlc-pill">Connecteur poste de travail</span>
          <h1>Installez le connecteur avant de continuer</h1>
          <p className="dlc-lede">
            L'étape que vous venez d'ouvrir utilise votre <b>carte CPS</b>. Or un navigateur ne peut
            pas dialoguer seul avec un lecteur de carte : il faut un petit programme installé sur
            votre poste pour faire le lien. C'est le connecteur MetaConnect. Vous l'installez une
            fois, et vous n'y pensez plus.
          </p>

          <div className="dlc-dl">
            <div className="dlc-btns">
              <a className="dlc-btn" href={INSTALLEUR_WINDOWS}>
                <svg viewBox="0 0 24 24" aria-hidden="true" className="dlc-os">
                  <path d="M3 5.6l7.4-1v7.2H3V5.6zM11.7 4.4L21 3v8.8h-9.3V4.4zM3 12.2h7.4v7.2L3 18.4v-6.2zM11.7 12.2H21V21l-9.3-1.3v-7.5z" />
                </svg>
                Télécharger pour Windows
              </a>
              <a className="dlc-btn dlc-btn2" href={INSTALLEUR_MACOS}>
                <svg viewBox="0 0 24 24" aria-hidden="true" className="dlc-os">
                  <path d="M16.3 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.9-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.2.9-1.2 1.3-2.5 1.3-2.5s-2.5-1-2.5-3.6zM14.1 5.1c.6-.8 1-1.9.9-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-.9 2.8 1 .1 2-.5 2.7-1.2z" />
                </svg>
                Télécharger pour macOS
              </a>
            </div>
            <p className="dlc-meta">
              Version {VERSION} &middot; Windows 10/11 et macOS &middot; installation en moins d'une
              minute
            </p>
          </div>

          <p className="dlc-back">
            Déjà installé ? Retournez sur l'onglet où vous étiez et cliquez sur{" "}
            <b>Réessayer</b> : votre saisie est conservée. Toujours pas détecté ?{" "}
            <Link to="/download/diagnostic">Testez votre poste en un clic</Link>.
          </p>
        </div>
      </section>

      <section className="dlc-sec dlc-alt">
        <div className="dlc-wrap">
          <div className="dlc-head">
            <h2>Pourquoi ce programme est nécessaire</h2>
            <p>
              Ce n'est pas une application de plus à apprendre. C'est la pièce qui manque entre votre
              navigateur et votre carte.
            </p>
          </div>
          <div className="dlc-grid">
            {RAISONS.map((r) => (
              <div className="dlc-card" key={r.t}>
                <h3>{r.t}</h3>
                <p>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dlc-sec">
        <div className="dlc-wrap">
          <div className="dlc-head">
            <h2>Trois étapes, une seule fois</h2>
          </div>
          <ol className="dlc-steps">
            {ETAPES.map((e, i) => (
              <li key={e.t}>
                <span className="dlc-num">{i + 1}</span>
                <div>
                  <h3>{e.t}</h3>
                  <p>{e.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="dlc-pre">
            <h3>Avant de commencer, vérifiez que vous avez</h3>
            <ul className="dlc-list">
              {PREREQUIS.map((x) => (
                <li key={x}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="dlc-sec dlc-alt">
        <div className="dlc-wrap">
          <div className="dlc-head">
            <h2>Si la carte n'est toujours pas détectée</h2>
            <p>
              Commencez par l'icône du connecteur, dans la zone des icônes près de l'horloge. Sa
              couleur vous dit où vous en êtes, et un clic dessus ouvre une fenêtre qui indique si le
              service est démarré.
            </p>
          </div>

          <ul className="dlc-etats">
            {ETATS.map((e) => (
              <li key={e.t}>
                <span
                  className={e.hex ? "dlc-dot" : "dlc-dot dlc-dot0"}
                  style={e.hex ? { background: e.hex } : undefined}
                  aria-hidden="true"
                />
                <div>
                  <h3>{e.t}</h3>
                  <p>{e.d}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="dlc-grid dlc-grid2">
            {SOUCIS.map((s) => (
              <div className="dlc-card" key={s.t}>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>

          <Link className="dlc-more" to="/download/verification">
            Vérifier les prérequis du poste et contrôler le service pas à pas
            <em>&rsaquo;</em>
          </Link>
        </div>
      </section>

      <section className="dlc-cta">
        <div className="dlc-wrap">
          <h2>Ça coince encore ?</h2>
          <p>
            Nous prenons la main sur votre poste et nous réglons l'installation avec vous. Comptez
            dix minutes.
          </p>
          <Link className="dlc-cbtn" to="/support-distant">
            Demander une assistance à distance
          </Link>
        </div>
      </section>
    </div>
  );
}

const CSS = `
.dlc{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--bsoft:#EAF4FF;--bline:#CFE4FF;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.dlc *{box-sizing:border-box}
.dlc h1,.dlc h2,.dlc h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.dlc h1{font-size:clamp(29px,3.6vw,40px);line-height:1.14;letter-spacing:-.02em}
.dlc p{margin:0;color:var(--muted);line-height:1.6}
.dlc ul,.dlc ol{list-style:none;margin:0;padding:0}
.dlc-wrap{max-width:1000px;margin:0 auto;padding:0 28px}

.dlc-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.dlc-hero{padding:82px 0 52px;text-align:center}
.dlc-hero h1{margin:16px auto 0;max-width:20ch}
.dlc .dlc-lede{max-width:64ch;margin:16px auto 0;font-size:16.5px}
.dlc .dlc-lede b{color:var(--text);font-weight:600}

.dlc-dl{margin:34px auto 0;background:#fff;border:1px solid var(--line);border-radius:18px;
  padding:26px 28px 22px;max-width:640px;box-shadow:0 18px 44px -34px rgba(15,23,42,.5)}
.dlc-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
.dlc-btn{display:inline-flex;align-items:center;gap:10px;border-radius:999px;background:var(--blue);
  color:#fff;font-weight:600;font-size:15px;text-decoration:none;padding:13px 24px;
  transition:background .18s}
.dlc-btn:hover{background:#0069D1;color:#fff}
.dlc-btn2{background:#fff;color:var(--text);border:1px solid var(--line)}
.dlc-btn2:hover{background:var(--ground);color:var(--text)}
.dlc-os{width:18px;height:18px;flex:0 0 18px;fill:currentColor}
.dlc .dlc-meta{margin-top:16px;font-size:13.2px}

.dlc .dlc-back{max-width:56ch;margin:22px auto 0;font-size:14.4px}
.dlc .dlc-back b{color:var(--text);font-weight:600}
.dlc-back a{color:#0B62C4;font-weight:600;text-decoration:none}
.dlc-back a:hover{text-decoration:underline}

.dlc-sec{padding:56px 0}
.dlc-alt{background:#fff;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.dlc-head{text-align:center;max-width:64ch;margin:0 auto}
.dlc-head h2{font-size:clamp(22px,2.6vw,30px);line-height:1.2;letter-spacing:-.018em}
.dlc-head p{margin-top:12px;font-size:15.5px}

.dlc-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:30px}
.dlc-grid2{grid-template-columns:repeat(2,1fr)}
.dlc-card{background:var(--ground);border:1px solid var(--line);border-radius:14px;padding:22px 22px 24px}
.dlc-alt .dlc-card{background:var(--ground)}
.dlc-card h3{font-size:16px;line-height:1.3;margin-bottom:9px}
.dlc-card p{font-size:13.8px;line-height:1.62}

.dlc-steps{display:grid;gap:12px;margin-top:30px}
.dlc-steps li{display:flex;gap:16px;align-items:flex-start;background:#fff;border:1px solid var(--line);
  border-radius:14px;padding:20px 22px}
.dlc-num{flex:0 0 30px;width:30px;height:30px;border-radius:50%;background:var(--bsoft);
  border:1px solid var(--bline);color:#0B62C4;font-weight:600;font-size:14px;
  display:grid;place-items:center;margin-top:1px}
.dlc-steps h3{font-size:16px;line-height:1.3;margin-bottom:7px}
.dlc-steps p{font-size:13.9px;line-height:1.62}

.dlc-pre{margin-top:22px;background:var(--bsoft);border:1px solid var(--bline);border-radius:16px;
  padding:24px 26px}
.dlc-pre h3{font-size:16px;margin-bottom:14px}
.dlc-list{display:grid;grid-template-columns:repeat(2,1fr);gap:11px 22px}
.dlc-list li{display:flex;gap:11px;align-items:flex-start;font-size:14.2px;line-height:1.55;
  color:#3D4759}
.dlc-list svg{width:16px;height:16px;flex:0 0 16px;margin-top:3px;stroke:#1E8A4C;fill:none;
  stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}

.dlc-etats{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:30px}
.dlc-etats li{display:flex;gap:13px;align-items:flex-start;background:var(--ground);
  border:1px solid var(--line);border-radius:14px;padding:20px 20px 22px}
.dlc-dot{flex:0 0 16px;width:16px;height:16px;border-radius:50%;margin-top:3px;
  box-shadow:inset 0 -3px 5px rgba(0,0,0,.22),inset 0 3px 4px rgba(255,255,255,.55)}
.dlc-dot0{background:#fff;border:1.5px dashed #B9C0CC;box-shadow:none}
.dlc-etats h3{font-size:15.4px;line-height:1.3;margin-bottom:7px}
.dlc-etats p{font-size:13.6px;line-height:1.6}

.dlc-more{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:20px;
  background:var(--bsoft);border:1px solid var(--bline);border-radius:13px;padding:16px 20px;
  text-decoration:none;color:#0B62C4;font-size:14.4px;font-weight:600;line-height:1.4;
  transition:background .18s}
.dlc-more:hover{background:#DBEBFF}
.dlc-more em{font-style:normal;font-size:19px;line-height:1}

.dlc-cta{background:var(--blue);padding:56px 0;text-align:center}
.dlc-cta h2{color:#fff;font-size:clamp(22px,2.6vw,30px);margin-bottom:12px}
.dlc-cta p{color:rgba(255,255,255,.9);font-size:15.5px;max-width:60ch;margin:0 auto 24px}
.dlc-cbtn{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;
  background:#fff;color:var(--blue);font-weight:600;font-size:15px;text-decoration:none;
  padding:13px 28px}
.dlc-cbtn:hover{background:#EAF4FF;color:var(--blue)}

@media (max-width:880px){
  .dlc-grid,.dlc-grid2,.dlc-etats{grid-template-columns:1fr}
  .dlc-list{grid-template-columns:1fr}
  .dlc-hero{padding:56px 0 40px}
}
@media (max-width:520px){
  .dlc-btns{flex-direction:column}
  .dlc-btn{justify-content:center}
  .dlc-dl{padding:22px 20px 18px}
}
.dlc a:focus-visible{outline:2px solid var(--blue);outline-offset:3px;border-radius:6px}
`;
